import React, { useState, useEffect } from "react";
import {
  Switch,
  Button,
  Snackbar,
  Alert,
  Slide,
  Grid,
  Avatar,
  Typography,
  Box,
  Chip,
  Fade,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import {
  ExpandMore,
  ExpandLess,
  InfoOutlined,
  CreditCardOff,
} from "@mui/icons-material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import AnseraLogo from "assets/images/Anser.png";
import axios from "axios";
import { useAuth } from "context/AuthContext";
import { FAST_API_BASE_URL } from "constants";
import { LAMBDA_BASE_URL } from "constants";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

const integrationFeatures = [
  {
    name: "Google Calendar",
    value: "google-calendar",
    logo: "https://upload.wikimedia.org/wikipedia/commons/a/a5/Google_Calendar_icon_%282020%29.svg",
    description: "Sync calendar events and schedule meetings seamlessly",
    category: "Productivity",
  },
];

const Integrations = () => {
  const [featureStates, setFeatureStates] = useState(
    integrationFeatures.reduce((acc, f) => {
      acc[f.name] = false;
      return acc;
    }, {})
  );

  const [expandedFeatures, setExpandedFeatures] = useState(
    integrationFeatures.reduce((acc, f) => {
      acc[f.name] = false;
      return acc;
    }, {})
  );

  const { currentDomain } = useAuth();
  const domainId = currentDomain?.domain_token;

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const [hasGoogleCredentials, setHasGoogleCredentials] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [showPopupHelp, setShowPopupHelp] = useState(false);
  const [pendingToggle, setPendingToggle] = useState(null);
  
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    featureName: "",
    action: "", 
  });
  
  const [toggleLoading, setToggleLoading] = useState({
    "Google Calendar": false,
  });

  useEffect(() => {
    const fetchInitialStatus = async () => {
      try {
        setCheckingStatus(true);
        const mcpRes = await axios.get(`${LAMBDA_BASE_URL}/mcp-status`, {
          headers: { "domain-token": domainId },
        });

        if (mcpRes?.data?.result) {
          const mcpEnabled = mcpRes.data.result.google_calendar_mcp === 1;
          setFeatureStates((prev) => ({
            ...prev,
            "Google Calendar": mcpEnabled,
          }));
        }
        await checkGoogleCredentials();
      } catch (err) {
        console.error("Failed to fetch initial status:", err);
        setFeatureStates((prev) => ({
          ...prev,
          "Google Calendar": false,
        }));
        setHasGoogleCredentials(false);
      } finally {
        setCheckingStatus(false);
      }
    };

    if (domainId) {
      fetchInitialStatus();
    }
  }, [domainId]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const googleAuth = params.get('google_auth');
    
    if (googleAuth === 'success') {
      checkGoogleCredentials().then(() => {
        setSnackbar({
          open: true,
          severity: "success",
          message: "Google Calendar connected successfully!",
        });
      });
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (googleAuth === 'error') {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to connect Google Calendar. Please try again.",
      });
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const checkGoogleCredentials = async () => {
    try {
      const res = await axios.get(
        `${FAST_API_BASE_URL}/api/mcp-credentials/google-calendar`,
        {
          headers: { "DOMAIN-TOKEN": domainId },
        }
      );

      if (res?.data?.result) {
        const credentials = res.data.result;
        const hasValidCredentials =
          credentials.client_id &&
          credentials.client_secret &&
          credentials.client_id.trim() !== "" &&
          credentials.client_secret.trim() !== "";

        setHasGoogleCredentials(hasValidCredentials);
        return hasValidCredentials;
      } else {
        setHasGoogleCredentials(false);
        return false;
      }
    } catch (err) {
      console.error("Failed to check Google credentials:", err);
      setHasGoogleCredentials(false);
      return false;
    }
  };

  const updateMCPStatus = async (enabled) => {
    try {
      await axios.patch(
        `${LAMBDA_BASE_URL}/mcp-status`,
        [{ google_calendar_mcp: enabled ? 1 : 0 }],
        { headers: { "domain-token": domainId } }
      );
      return true;
    } catch (err) {
      console.error("Failed to update MCP status:", err);
      throw err;
    }
  };

  const handleExpand = (name, value) => {
    setExpandedFeatures((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  const handleConnectGoogle = async () => {
    setLoading(true);

    try {
      const res = await axios.get(
        `${FAST_API_BASE_URL}/api/google-calendar/auth`,
        {
          headers: { "DOMAIN-TOKEN": domainId },
        }
      );

      if (res?.data?.result?.auth_url) {
        const width = 600;
        const height = 700;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;

        const popup = window.open(
          res.data.result.auth_url,
          "google_calendar_auth",
          `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,menubar=no,scrollbars=yes,resizable=yes`
        );

        if (!popup || popup.closed || typeof popup.closed === "undefined") {
          setShowPopupHelp(true);
          setSnackbar({
            open: true,
            severity: "warning",
            message:
              "Popup was blocked by your browser. Please enable popups for this site.",
          });
          setLoading(false);
          return;
        }

        const handleMessage = (event) => {
          const validOrigins = [
            "http://localhost:8000",
            "http://localhost:3000",
            "https://api.ansera.ai",
          ];

          if (!validOrigins.includes(event.origin)) {
            console.warn("Message from unknown origin:", event.origin);
            return;
          }

          if (event.data.type === "GOOGLE_CALENDAR_AUTH_SUCCESS") {
            updateMCPStatus(true)
              .then(() => {
                setFeatureStates((prev) => ({
                  ...prev,
                  "Google Calendar": true,
                }));
                setToggleLoading((prev) => ({
                  ...prev,
                  "Google Calendar": false,
                }));
                checkGoogleCredentials();
                setSnackbar({
                  open: true,
                  severity: "success",
                  message: "Google Calendar connected successfully!",
                });
                setLoading(false);
                setShowPopupHelp(false);
                setPendingToggle(null);
              })
              .catch((err) => {
                console.error("Failed to update MCP status after OAuth:", err);
                setSnackbar({
                  open: true,
                  severity: "error",
                  message: "Connected but failed to enable integration.",
                });
                setFeatureStates((prev) => ({
                  ...prev,
                  "Google Calendar": false,
                }));
                setToggleLoading((prev) => ({
                  ...prev,
                  "Google Calendar": false,
                }));
                setLoading(false);
              });

            window.removeEventListener("message", handleMessage);

            if (popup && !popup.closed) {
              popup.close();
            }
          } else if (event.data.type === "GOOGLE_CALENDAR_AUTH_ERROR") {
            setSnackbar({
              open: true,
              severity: "error",
              message: event.data.message || "Failed to connect Google Calendar.",
            });
            setLoading(false);
            setToggleLoading((prev) => ({
              ...prev,
              "Google Calendar": false,
            }));
            setPendingToggle(null);
            window.removeEventListener("message", handleMessage);
          }
        };

        window.addEventListener("message", handleMessage);

        const checkPopupClosed = setInterval(() => {
          try {
            if (popup.closed) {
              clearInterval(checkPopupClosed);
              window.removeEventListener("message", handleMessage);

              if (loading) {
                setSnackbar({
                  open: true,
                  severity: "info",
                  message: "Authorization window was closed.",
                });
                setLoading(false);
                if (pendingToggle) {
                  setFeatureStates((prev) => ({
                    ...prev,
                    "Google Calendar": false,
                  }));
                  setToggleLoading((prev) => ({
                    ...prev,
                    "Google Calendar": false,
                  }));
                  setPendingToggle(null);
                }
              }
            }
          } catch (e) {
            clearInterval(checkPopupClosed);
          }
        }, 1000);
      } else {
        throw new Error("No auth URL received");
      }
    } catch (err) {
      console.error("Failed to initiate Google auth:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to connect to Google Calendar.",
      });
      setLoading(false);
      if (pendingToggle) {
        setFeatureStates((prev) => ({
          ...prev,
          "Google Calendar": false,
        }));
        setToggleLoading((prev) => ({
          ...prev,
          "Google Calendar": false,
        }));
        setPendingToggle(null);
      }
    }
  };

  const handleDisconnectGoogle = async () => {
    try {
      await axios.delete(`${FAST_API_BASE_URL}/api/mcp-credentials/google-calendar`, {
        headers: { "DOMAIN-TOKEN": domainId },
      });

      setHasGoogleCredentials(false);
      setSnackbar({
        open: true,
        severity: "success",
        message: "Google Calendar credentials deleted permanently!",
      });
    } catch (err) {
      console.error("Failed to disconnect Google:", err);
      throw err;
    }
  };

  const handleToggle = async (name, value) => {
    const newValue = !featureStates[name];
    const isEnabling = newValue && !featureStates[name];
    const isDisabling = !newValue && featureStates[name];

    setToggleLoading((prev) => ({
      ...prev,
      [name]: true,
    }));

    if (isEnabling) {
      if (hasGoogleCredentials) {
        try {
          await updateMCPStatus(true);
          setFeatureStates((prev) => ({
            ...prev,
            [name]: newValue,
          }));
          setExpandedFeatures((prev) => ({
            ...prev,
            [name]: true,
          }));
          setSnackbar({
            open: true,
            severity: "success",
            message: `Google Calendar enabled successfully!`,
          });
        } catch (err) {
          console.error("Failed to update MCP status:", err);
          setSnackbar({
            open: true,
            severity: "error",
            message: "Failed to enable Google Calendar.",
          });
          setFeatureStates((prev) => ({
            ...prev,
            [name]: !newValue,
          }));
        } finally {
          setToggleLoading((prev) => ({
            ...prev,
            [name]: false,
          }));
        }
      } else {
        setFeatureStates((prev) => ({
          ...prev,
          [name]: newValue,
        }));
        setPendingToggle(name);
        setExpandedFeatures((prev) => ({
          ...prev,
          [name]: true,
        }));

        setTimeout(() => {
          handleConnectGoogle();
        }, 300);
      }
    } else if (isDisabling) {
      setConfirmDialog({
        open: true,
        featureName: name,
        action: "disable",
      });
      setToggleLoading((prev) => ({
        ...prev,
        [name]: false,
      }));
    }
  };

  const handleConfirmDisable = async (permanentDelete = false) => {
    const { featureName } = confirmDialog;
    setToggleLoading((prev) => ({
      ...prev,
      [featureName]: true,
    }));

    try {
      await updateMCPStatus(false);
      
      if (permanentDelete) {
        await handleDisconnectGoogle();
      }
      setFeatureStates((prev) => ({
        ...prev,
        [featureName]: false,
      }));

      setSnackbar({
        open: true,
        severity: "success",
        message: permanentDelete
          ? `Google Calendar disconnected and credentials deleted permanently!`
          : `Google Calendar disabled temporarily!`,
      });
    } catch (err) {
      console.error("Failed to disable Google Calendar:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message: permanentDelete
          ? "Failed to delete Google Calendar credentials."
          : "Failed to disable Google Calendar.",
      });
      setFeatureStates((prev) => ({
        ...prev,
        [featureName]: true,
      }));
    } finally {
      setToggleLoading((prev) => ({
        ...prev,
        [featureName]: false,
      }));
      setConfirmDialog({
        open: false,
        featureName: "",
        action: "",
      });
    }
  };

  const handleConfirmDialogClose = () => {
    setConfirmDialog({
      open: false,
      featureName: "",
      action: "",
    });
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox
        sx={{
          backgroundColor: "#fff",
          p: 4,
          borderRadius: "20px",
          boxShadow: "0px 10px 40px rgba(0,0,0,0.05)",
          minHeight: "500px",
        }}
      >
        <Dialog
          open={confirmDialog.open}
          onClose={handleConfirmDialogClose}
          aria-labelledby="confirm-dialog-title"
          aria-describedby="confirm-dialog-description"
          PaperProps={{
            sx: {
              borderRadius: "16px",
              padding: "8px",
              boxShadow: "0px 10px 40px rgba(0,0,0,0.15)",
              border: "1px solid #e5f1f1",
            },
          }}
        >
          <DialogTitle
            id="confirm-dialog-title"
            sx={{
              fontWeight: "bold",
              color: "#219297",
              padding: "24px 24px 16px 24px",
            }}
          >
            Disable Google Calendar
          </DialogTitle>
          <DialogContent sx={{ padding: "0 24px 20px 24px" }}>
            <DialogContentText
              id="confirm-dialog-description"
              sx={{ color: "#666", fontSize: "0.95rem" }}
            >
              How would you like to disable Google Calendar?
            </DialogContentText>
            <Box sx={{ mt: 2, p: 2, backgroundColor: "#f9f9f9", borderRadius: "8px" }}>
              <Typography variant="body2" sx={{ color: "#424242", fontWeight: 500 }}>
                <strong>Disable temporarily:</strong> Keeps your credentials stored for quick re-enabling.
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#666", mt: 1, fontStyle: "italic" }}
              >
                You can enable it again anytime without re-authenticating.
              </Typography>
            </Box>
            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: "#fcfcfcff",
                borderRadius: "8px",
                border: "1px solid #ff8080ff",
              }}
            >
              <Typography variant="body2" sx={{ color: "#e60000ff", fontWeight: 500 }}>
                <strong>Permanently delete credentials:</strong> Removes all Google Calendar access from our system.
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#e65100", mt: 1, fontStyle: "italic" }}
              >
                You'll need to re-authenticate with Google if you want to enable it again.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ padding: "16px 24px 24px 24px", gap: 1 }}>
            <Button
              onClick={handleConfirmDialogClose}
              sx={{
                color: "#666",
                fontWeight: 600,
                textTransform: "none",
                borderRadius: "8px",
                padding: "8px 20px",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleConfirmDisable(false)}
              variant="outlined"
              sx={{
                color: "#219297",
                borderColor: "#219297",
                fontWeight: 600,
                textTransform: "none",
                borderRadius: "8px",
                padding: "8px 20px",
                "&:hover": {
                  borderColor: "#219297",
                  backgroundColor: "#e5f1f1",
                },
              }}
            >
              Disable Temporarily
            </Button>
            <Button
              onClick={() => handleConfirmDisable(true)}
              variant="contained"
              sx={{
                backgroundColor: "#d32f2f",
                fontWeight: 600,
                textTransform: "none",
                color: "#fff",
                borderRadius: "8px",
                padding: "8px 20px",
                "&:hover": {
                  backgroundColor: "#b71c1c",
                },
              }}
            >
              Delete Permanently
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          TransitionComponent={(props) => <Slide {...props} direction="down" />}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            variant="filled"
            sx={{
              backgroundColor:
                snackbar.severity === "success" ? "#219297" : "#d32f2f",
              color: "#fff",
              borderRadius: "12px",
              boxShadow: "0px 4px 20px rgba(0,0,0,0.15)",
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        <SoftBox mb={4}>
          <SoftBox display="flex" alignItems="center" gap={2} mb={1}>
            <Box
              sx={{
                borderRadius: "12px",
                p: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img src={AnseraLogo} alt="Ansera Logo" width={64} height={30} />
            </Box>
            <SoftTypography variant="h5" fontWeight="bold" color="dark">
              Manage Integrations
            </SoftTypography>
          </SoftBox>
          <SoftTypography
            variant="body2"
            color="text"
            sx={{ opacity: 0.7, mb: 2 }}
          >
            Enable or disable integrations with third-party tools.
          </SoftTypography>
          <Box
            sx={{
              height: "2px",
              width: "30%",
              background:
                "linear-gradient(90deg, #219297 0%, transparent 100%)",
            }}
          />
        </SoftBox>

        <Collapse in={showPopupHelp}>
          <Alert
            severity="info"
            sx={{
              mb: 3,
              borderRadius: "16px",
              border: "2px solid #219297",
              backgroundColor: "#e0f5f5",
              boxShadow: "0px 4px 12px rgba(33, 146, 151, 0.15)",
            }}
            icon={<InfoOutlined sx={{ fontSize: 28, color: "#219297" }} />}
            onClose={() => setShowPopupHelp(false)}
          >
            <Typography
              variant="body1"
              fontWeight={700}
              gutterBottom
              sx={{ color: "#145d61", mb: 2 }}
            >
              🚫 Popup Blocked - Enable Popups to Continue
            </Typography>

            <Typography
              variant="body2"
              sx={{ color: "#2c5f5f", mb: 2, lineHeight: 1.6 }}
            >
              Your browser blocked the authorization window. Please allow
              popups for this site to connect your Google Calendar.
            </Typography>

            <Box
              sx={{
                backgroundColor: "#fff",
                borderRadius: "12px",
                p: 2.5,
                border: "1px solid #a8d5d8",
              }}
            >
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ color: "#219297", mb: 2 }}
              >
                How to Enable Popups:
              </Typography>

              <Box
                component="ul"
                sx={{ m: 0, pl: 2.5, "& li": { mb: 1.5 } }}
              >
                <li>
                  <Typography variant="body2" sx={{ color: "#424242" }}>
                    <strong style={{ color: "#219297" }}>Chrome / Edge:</strong>{" "}
                    Look for the popup icon
                    <Box
                      component="span"
                      sx={{
                        display: "inline-block",
                        mx: 0.5,
                        px: 1,
                        py: 0.3,
                        borderRadius: "4px",
                        fontFamily: "monospace",
                      }}
                    >
                      <CreditCardOff />
                    </Box>
                    in your address bar (top right), click it, and select{" "}
                    <strong>"Always allow popups from this site"</strong>
                  </Typography>
                </li>

                <li>
                  <Typography variant="body2" sx={{ color: "#424242" }}>
                    <strong style={{ color: "#1a767a" }}>Firefox:</strong> Click
                    the <strong>"Preferences"</strong> or{" "}
                    <strong>"Options"</strong> button in the address bar, then
                    select <strong>"Allow popups for this site"</strong>
                  </Typography>
                </li>

                <li>
                  <Typography variant="body2" sx={{ color: "#424242" }}>
                    <strong style={{ color: "#0d5457" }}>Safari:</strong> Go to{" "}
                    <strong>Safari menu</strong> → <strong>Settings</strong> →{" "}
                    <strong>Websites</strong> → <strong>Pop-up Windows</strong>,
                    then set this site to <strong>"Allow"</strong>
                  </Typography>
                </li>
              </Box>
            </Box>

            <Box
              sx={{
                mt: 2,
                p: 1.5,
                background: "linear-gradient(135deg, #fff5e6 0%, #ffe8cc 100%)",
                borderRadius: "8px",
                border: "1px solid #ffb84d",
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "#cc6600", fontWeight: 600 }}
              >
                After enabling popups, toggle the Google Calendar switch again
                to reconnect.
              </Typography>
            </Box>
          </Alert>
        </Collapse>

        <Grid container spacing={3}>
          {integrationFeatures.map((feature) => (
            <Grid item xs={12} key={feature.name}>
              <Fade in timeout={500}>
                <SoftBox
                  sx={{
                    border: "1px solid",
                    borderColor: featureStates[feature.name]
                      ? "#219297"
                      : "#e5f1f1",
                    borderRadius: "16px",
                    p: 2,
                    transition: "all 0.3s ease",
                    background: "linear-gradient(135deg, #f0fafa 0%, #fff 100%)",
                    "&:hover": {
                      boxShadow: "0px 8px 24px rgba(33, 146, 151, 0.12)",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <SoftBox
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={1}
                  >
                    <SoftBox display="flex" alignItems="center" gap={2}>
                      <Avatar
                        src={feature.logo}
                        alt={feature.name}
                        sx={{
                          width: 48,
                          height: 48,
                          boxShadow: "0px 4px 12px rgba(0,0,0,0.08)",
                          background: "#fff",
                          p: 0.5,
                        }}
                        variant="rounded"
                      />
                      <Box>
                        <SoftBox display="flex" alignItems="center" gap={1.5}>
                          <SoftTypography variant="h6" fontWeight="bold">
                            {feature.name}
                          </SoftTypography>
                          {featureStates[feature.name] && (
                            <Chip
                              label="Active"
                              size="small"
                              sx={{
                                height: "20px",
                                backgroundColor: "#219297",
                                color: "#fff",
                                fontSize: "0.7rem",
                                fontWeight: 600,
                              }}
                            />
                          )}
                          {pendingToggle === feature.name && (
                            <Chip
                              label="Authorizing..."
                              size="small"
                              sx={{
                                height: "20px",
                                backgroundColor: "#FF9800",
                                color: "#fff",
                                fontSize: "0.7rem",
                                fontWeight: 600,
                                animation: "pulse 1.5s infinite",
                              }}
                            />
                          )}
                        </SoftBox>
                        <SoftTypography
                          variant="caption"
                          color="text"
                          sx={{ opacity: 0.7 }}
                        >
                          {feature.description}
                        </SoftTypography>
                      </Box>
                    </SoftBox>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Box sx={{ position: "relative" }}>
                        <Switch
                          checked={featureStates[feature.name]}
                          onChange={() =>
                            handleToggle(feature.name, feature.value)
                          }
                          disabled={
                            loading ||
                            checkingStatus ||
                            pendingToggle === feature.name ||
                            toggleLoading[feature.name]
                          }
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: "#219297",
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                              { backgroundColor: "#219297" },
                            "& .MuiSwitch-track": { backgroundColor: "#e0e0e0" },
                          }}
                        />
                        {/* {toggleLoading[feature.name] && (
                          <CircularProgress
                            size={24}
                            sx={{
                              color: "#219297",
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              marginTop: "-12px",
                              marginLeft: "-12px",
                            }}
                          />
                        )} */}
                      </Box>
                      <IconButton
                        onClick={() => handleExpand(feature.name, feature.value)}
                        disabled={loading || toggleLoading[feature.name]}
                      >
                        {expandedFeatures[feature.name] ? (
                          <ExpandLess />
                        ) : (
                          <ExpandMore />
                        )}
                      </IconButton>
                    </Box>
                  </SoftBox>

                  {feature.name === "Google Calendar" &&
                    expandedFeatures["Google Calendar"] && (
                      <Fade in timeout={300}>
                        <Box
                          mt={3}
                          pt={3}
                          sx={{ borderTop: "1px solid #e5f1f1" }}
                        >
                          <SoftBox
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            mb={3}
                          >
                            <Typography
                              variant="subtitle1"
                              fontWeight={700}
                              sx={{ color: "#219297" }}
                            >
                              Connection Status
                            </Typography>
                          </SoftBox>

                          {checkingStatus ? (
                            <Typography
                              variant="body2"
                              sx={{ color: "#666", fontStyle: "italic" }}
                            >
                              Checking connection status...
                            </Typography>
                          ) : loading ||
                            pendingToggle === "Google Calendar" ||
                            toggleLoading["Google Calendar"] ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                backgroundColor: "#fff3e0",
                                px: 2.5,
                                py: 1.5,
                                borderRadius: "10px",
                              }}
                            >
                              <CircularProgress size={20} sx={{ color: "#e65100",marginRight:"1.5rem" }} />
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                sx={{ color: "#e65100" }}
                              >
                                {pendingToggle
                                  ? "Waiting for authorization..."
                                  : "Processing..."}
                              </Typography>
                            </Box>
                          ) : featureStates["Google Calendar"] &&
                            hasGoogleCredentials ? (
                            <SoftBox>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  backgroundColor: "#e8f5e9",
                                  px: 2.5,
                                  py: 1.5,
                                  borderRadius: "10px",
                                  mb: 2,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  sx={{ color: "#2e7d32" }}
                                >
                                  ✓ Connected to Google Calendar
                                </Typography>
                              </Box>
                              <Typography
                                variant="body2"
                                sx={{ color: "#666", fontStyle: "italic" }}
                              >
                                Your Google Calendar is connected and ready to
                                sync events seamlessly.
                              </Typography>
                            </SoftBox>
                          ) : (
                            <Box
                              sx={{
                                backgroundColor: "#fff3e0",
                                px: 2.5,
                                py: 1.5,
                                borderRadius: "10px",
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ color: "#e65100", fontStyle: "italic" }}
                              >
                                {hasGoogleCredentials
                                  ? "Google Calendar is not enabled. Enable the toggle above to connect."
                                  : "Not connected. Enable the toggle above to connect Google Calendar."}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Fade>
                    )}
                </SoftBox>
              </Fade>
            </Grid>
          ))}
        </Grid>

        <style>
          {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.7; }
            100% { opacity: 1; }
          }
        `}
        </style>
      </SoftBox>
    </DashboardLayout>
  );
};

export default Integrations;